import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Send, Plus, Bot, Loader2, ChevronRight, Image as ImageIcon,
  Paperclip, Folder, X, Trash2, Sparkles, History, Edit3
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from '../../utils/toast';
const API = window.__API_BASE;

// ──────────────────────────────────────────────────────────────────────────
// EipiAssistantPanel — ChatGPT-like floating assistant.
//   Capabilities: text (GPT-5.5), vision (image attachments), image generation
//   (gpt-image-1), multi-turn persistence (MongoDB), session history sidebar.
//   Trigger: parent owns `isOpen` + `onClose`. Renders nothing when isOpen=false.
// ──────────────────────────────────────────────────────────────────────────

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    const b64 = String(result).split(',')[1] || '';
    resolve(b64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export function EipiAssistantPanel({ isOpen, onClose, userInitial }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{name, mime_type, image_base64, preview}]
  const [mode, setMode] = useState('text'); // 'text' | 'image'
  const [plusOpen, setPlusOpen] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [projectsShortlist, setProjectsShortlist] = useState([]);
  const [showProjects, setShowProjects] = useState(false);

  const fileInputRef = useRef(null);
  const plusRef = useRef(null);
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/assistant/sessions`);
      setSessions(data || []);
    } catch (e) { /* silent */ }
  }, []);

  // Async image-gen poller — short-circuits the long HTTP keep-alive risk on
  // mobile/CDN paths. Each poll is a tiny 1-line response, so dropped network
  // hops just retry on the next tick.
  // Declared BEFORE loadMessages because that callback references it.
  const pollImageJob = useCallback((jobId) => {
    let attempts = 0;
    const maxAttempts = 60; // 60 polls × 3s = 3 min
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/api/assistant/image-job/${jobId}`, { timeout: 8000 });
        if (data.status === 'done') {
          setMessages(prev => prev.map(m => m.image_job_id === jobId ? {
            ...m,
            status: 'done',
            content: `Generated image`,
            image_url: data.image_url || null,
            image_base64: data.image_base64 || null,
          } : m));
          return;
        }
        if (data.status === 'failed') {
          setMessages(prev => prev.map(m => m.image_job_id === jobId ? {
            ...m,
            status: 'failed',
            content: `⚠️ ${data.error || 'Image generation failed — please retry.'}`,
          } : m));
          return;
        }
      } catch (_e) { /* transient network — keep polling */ }
      if (attempts < maxAttempts) {
        setTimeout(tick, 3000);
      } else {
        setMessages(prev => prev.map(m => m.image_job_id === jobId && m.status === 'pending' ? {
          ...m,
          status: 'failed',
          content: '⚠️ Image still generating after 3 min. Refresh the chat to check again.',
        } : m));
      }
    };
    setTimeout(tick, 2000);
  }, []);

  const loadMessages = useCallback(async (sid) => {
    if (!sid) { setMessages([]); return; }
    try {
      const { data } = await axios.get(`${API}/api/assistant/sessions/${sid}/messages`);
      setMessages(data || []);
      // Resume polling for any still-pending image jobs in this session.
      (data || []).forEach(m => {
        if (m.image_job_id && m.status === 'pending') {
          pollImageJob(m.image_job_id);
        }
      });
    } catch (e) { setMessages([]); }
  }, [pollImageJob]);

  useEffect(() => {
    if (isOpen) loadSessions();
  }, [isOpen, loadSessions]);

  useEffect(() => {
    loadMessages(activeSessionId);
  }, [activeSessionId, loadMessages]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // close the + menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (plusRef.current && !plusRef.current.contains(e.target)) {
        setPlusOpen(false);
        setShowProjects(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const ok = ['image/png', 'image/jpeg', 'image/webp'];
    const next = [];
    for (const f of files.slice(0, 4)) {
      if (!ok.includes(f.type)) { toast.error(`Skipped ${f.name} — only PNG/JPG/WEBP`); continue; }
      try {
        const b64 = await fileToBase64(f);
        next.push({ name: f.name, mime_type: f.type, image_base64: b64, preview: `data:${f.type};base64,${b64}` });
      } catch { /* skip */ }
    }
    setAttachments(prev => [...prev, ...next].slice(0, 4));
    e.target.value = '';
    setPlusOpen(false);
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const sendMessage = async () => {
    if (sending) return;
    const text = input.trim();
    if (!text && attachments.length === 0) return;

    // optimistic user turn
    const optimistic = {
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
      _local_attachments: attachments.map(a => a.preview),
    };
    setMessages(prev => [...prev, optimistic]);
    setInput('');
    setSending(true);
    const sentAttachments = attachments;
    setAttachments([]);

    try {
      const { data } = await axios.post(`${API}/api/assistant/chat`, {
        session_id: activeSessionId || undefined,
        message: text,
        mode,
        attachments: sentAttachments.map(a => ({ image_base64: a.image_base64, mime_type: a.mime_type })),
      }, {
        // Text + vision return in ~3s; the POST for image mode now returns in
        // <2s (job is async + polled) so 60s is generous for both paths.
        timeout: 60000,
      });
      const assistantTurn = {
        role: 'assistant',
        content: data.content || '',
        image_url: data.image_url || null,
        image_base64: data.image_base64 || null,
        image_job_id: data.image_job_id || null,
        status: data.status || null,
        created_at: data.created_at,
      };
      setMessages(prev => [...prev, assistantTurn]);
      if (!activeSessionId) {
        setActiveSessionId(data.session_id);
        loadSessions();
      } else {
        loadSessions(); // refresh order
      }
      // Image-gen is async — kick off a polling loop that updates the
      // assistant turn in-place when the background job finishes.
      if (data.image_job_id && data.status === 'pending') {
        pollImageJob(data.image_job_id);
      }
      // image mode is one-shot — flip back to text afterwards
      if (mode === 'image') setMode('text');
    } catch (e) {
      // Surface real failure reason instead of generic "unreachable".
      let msg;
      if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '')) {
        msg = mode === 'image'
          ? 'Image generation timed out (>3 min). The model may be overloaded — please retry.'
          : 'Request timed out — please retry.';
      } else if (e.response?.data?.detail) {
        msg = String(e.response.data.detail);
      } else if (e.response?.status) {
        msg = `Server returned ${e.response.status}. Please retry.`;
      } else if (e.message) {
        msg = `Network error: ${e.message}`;
      } else {
        msg = 'Assistant unreachable';
      }
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${msg}`, created_at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const newChat = () => {
    setActiveSessionId(null);
    setMessages([]);
    setInput('');
    setAttachments([]);
    setMode('text');
    setShowSessions(false);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/assistant/sessions/${sid}`);
      if (activeSessionId === sid) newChat();
      loadSessions();
    } catch { toast.error('Delete failed'); }
  };

  const openProjectsMenu = async () => {
    try {
      const { data } = await axios.get(`${API}/api/assistant/projects-shortlist?limit=15`);
      setProjectsShortlist(data || []);
      setShowProjects(true);
    } catch { toast.error('Could not load projects'); }
  };

  if (!isOpen) return null;

  return (
    <aside
      className="fixed md:relative inset-y-0 right-0 z-50 w-96 max-w-full flex-shrink-0 flex flex-col"
      style={{ background: '#0B0F19', borderLeft: '1px solid rgba(14, 165, 233, 0.15)' }}
      data-testid="eipi-assistant-panel"
    >
      {/* Mobile backdrop */}
      <div className="fixed inset-0 bg-black/60 z-[-1] md:hidden" onClick={onClose} />

      {/* Header */}
      <div className="h-14 px-3 border-b flex items-center justify-between gap-2" style={{ background: 'rgba(19, 27, 44, 0.6)', borderColor: 'rgba(14,165,233,0.15)' }}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            onClick={() => setShowSessions(s => !s)}
            className={`relative flex items-center gap-1.5 px-2 py-1 rounded-sm transition-colors ${showSessions ? 'bg-ai-accent/20 text-ai-accent' : 'hover:bg-white/10 text-ai-text-secondary'}`}
            title="Chat history"
            data-testid="assistant-sessions-toggle"
          >
            <History className="w-3.5 h-3.5" />
            <span className="text-[10px] font-ai uppercase tracking-wider hidden sm:inline">History</span>
            {sessions.length > 0 && (
              <span className="text-[9px] font-mono px-1 rounded bg-ai-accent/30 text-ai-accent">{sessions.length}</span>
            )}
          </button>
          <img src="https://customer-assets.emergentagent.com/job_a2435b19-3cc0-4261-90b4-1cf9d208aea9/artifacts/h74r9xmb_image.png" alt="EIPI" className="w-7 h-7 rounded-sm object-cover border border-ai-border flex-shrink-0" />
          <span className="font-ai text-sm font-medium text-ai-text tracking-tight truncate hidden sm:inline">EIPI</span>
          <span className="flex items-center gap-1 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[9px] font-ai text-green-400 uppercase">AI</span>
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={newChat}
            className="flex items-center gap-1.5 px-2 py-1 rounded-sm hover:bg-white/10 transition-colors text-ai-accent"
            title="Start a new chat"
            data-testid="assistant-new-chat"
          >
            <Edit3 className="w-3.5 h-3.5" />
            <span className="text-[10px] font-ai uppercase tracking-wider hidden sm:inline">New</span>
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-sm" data-testid="close-chat-btn">
            <ChevronRight className="w-4 h-4 text-ai-text-secondary" />
          </button>
        </div>
      </div>

      {/* Sessions drawer */}
      {showSessions && (
        <div className="border-b max-h-[420px] overflow-y-auto" style={{ background: 'rgba(0,0,0,0.35)', borderColor: 'rgba(255,255,255,0.06)' }} data-testid="assistant-sessions-list">
          <div className="sticky top-0 px-3 py-2 text-[10px] uppercase tracking-wider text-white/50 font-ai border-b border-white/5" style={{ background: 'rgba(0,0,0,0.4)' }}>
            Recent chats · {sessions.length}
          </div>
          {sessions.length === 0 ? (
            <p className="px-4 py-4 text-[11px] text-white/40 italic">No previous chats yet — start one below.</p>
          ) : sessions.map(s => (
            <div
              key={s.id}
              onClick={() => { setActiveSessionId(s.id); setShowSessions(false); }}
              className={`flex items-center justify-between gap-2 px-3 py-2.5 cursor-pointer border-b border-white/5 hover:bg-white/5 transition-colors ${activeSessionId === s.id ? 'bg-ai-accent/10 border-l-2 border-l-ai-accent' : ''}`}
              data-testid={`assistant-session-${s.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white/90 truncate font-medium">{s.title}</p>
                <p className="text-[10px] text-white/40 mt-0.5">
                  {s.message_count} {s.message_count === 1 ? 'msg' : 'msgs'} · {new Date(s.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <button
                onClick={(e) => deleteSession(s.id, e)}
                className="p-1.5 hover:bg-red-500/20 rounded-sm opacity-50 hover:opacity-100"
                title="Delete chat"
                data-testid={`assistant-session-delete-${s.id}`}
              >
                <Trash2 className="w-3 h-3 text-red-300" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar" data-testid="assistant-messages">
        {messages.length === 0 && !sending && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <Sparkles className="w-10 h-10 text-ai-accent/40 mb-3" />
            <p className="text-sm font-ai text-white/70 mb-1">Ask anything</p>
            <p className="text-[11px] text-white/40">Powered by AI · attach images, generate images, or just chat.</p>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start gap-2'}>
            {m.role === 'assistant' && (
              <div className="w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(14, 165, 233, 0.2)', border: '1px solid rgba(14, 165, 233, 0.3)' }}>
                <Bot className="w-3.5 h-3.5 text-ai-accent" />
              </div>
            )}
            <div
              className={`px-3 py-2 max-w-[85%] text-sm rounded-lg ${m.role === 'user' ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
              style={m.role === 'user'
                ? { background: 'rgba(212, 163, 115, 0.10)', border: '1px solid rgba(212, 163, 115, 0.25)', color: '#E8DCCA' }
                : { background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.20)', color: '#E5E7EB' }
              }
              data-testid={`assistant-msg-${m.role}`}
            >
              {/* Image attachments from this user turn (local preview only) */}
              {m._local_attachments?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {m._local_attachments.map((src, i) => (
                    <img key={i} src={src} alt="attachment" className="w-16 h-16 object-cover rounded-sm border border-white/10" />
                  ))}
                </div>
              )}
              {m.content && (
                m.role === 'user' ? (
                  <p className="whitespace-pre-wrap break-words leading-snug">{m.content}</p>
                ) : (
                  <div className="prose prose-invert prose-sm max-w-none
                    [&_p]:mb-2 [&_p:last-child]:mb-0
                    [&_ul]:mb-2 [&_ul]:pl-4 [&_ul]:list-disc [&_ol]:mb-2 [&_ol]:pl-4
                    [&_li]:mb-1 [&_strong]:text-ai-accent [&_strong]:font-semibold
                    [&_code]:bg-black/40 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded-sm [&_code]:text-ai-accent [&_code]:text-xs
                    [&_pre]:bg-black/50 [&_pre]:p-2 [&_pre]:rounded [&_pre]:my-2 [&_pre]:overflow-x-auto">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  </div>
                )
              )}
              {/* Generated image */}
              {(m.image_url || m.image_base64) && (
                <img
                  src={m.image_url || `data:image/png;base64,${m.image_base64}`}
                  alt="generated"
                  className="mt-2 w-full max-w-[300px] rounded-md border border-white/10 cursor-zoom-in"
                  onClick={() => window.open(m.image_url || `data:image/png;base64,${m.image_base64}`, '_blank')}
                  data-testid="assistant-generated-image"
                />
              )}
              {/* Async image-gen still in progress */}
              {m.role === 'assistant' && m.status === 'pending' && m.image_job_id && (
                <div className="mt-2 flex items-center gap-2 text-[11px] text-amber-300/90" data-testid="assistant-pending-image">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <PendingImageStatus startedAt={m.created_at} />
                </div>
              )}
            </div>
            {m.role === 'user' && (
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ml-2 mt-0.5" style={{ background: 'rgba(212, 163, 115, 0.20)', border: '1px solid rgba(212, 163, 115, 0.30)' }}>
                <span className="text-xs font-human" style={{ color: '#D4A373' }}>{userInitial || 'U'}</span>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm flex items-center justify-center" style={{ background: 'rgba(14, 165, 233, 0.2)' }}>
              <Bot className="w-3.5 h-3.5 text-ai-accent" />
            </div>
            <div className="rounded-lg rounded-tl-sm px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.20)' }}>
              <Loader2 className="w-3 h-3 animate-spin text-ai-accent" />
              <span className="text-xs text-ai-text-secondary">
                {mode === 'image' ? <ImageGenProgress /> : 'Thinking…'}
              </span>
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div className="px-3 pt-2 flex flex-wrap gap-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }} data-testid="assistant-attachments-row">
          {attachments.map((a, idx) => (
            <div key={idx} className="relative group">
              <img src={a.preview} alt={a.name} className="w-14 h-14 object-cover rounded-sm border border-white/15" />
              <button
                onClick={() => removeAttachment(idx)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center opacity-0 group-hover:opacity-100"
                title="Remove"
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* Mode indicator */}
      {mode === 'image' && (
        <div className="px-3 pt-2">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="assistant-image-mode-badge">
            <ImageIcon className="w-3 h-3" /> Image generation mode · next message will create an image
          </span>
        </div>
      )}

      {/* Composer */}
      <div className="p-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="relative flex items-center gap-2 rounded-full pl-2 pr-1 py-1" style={{ background: 'rgba(19, 27, 44, 0.7)', border: '1px solid rgba(14, 165, 233, 0.25)' }}>
          {/* + menu */}
          <div className="relative" ref={plusRef}>
            <button
              onClick={() => { setPlusOpen(o => !o); setShowProjects(false); }}
              className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              title="Add or create"
              data-testid="assistant-plus-btn"
            >
              <Plus className="w-4 h-4 text-ai-accent" />
            </button>
            {plusOpen && (
              <div
                className="absolute bottom-10 left-0 w-56 rounded-lg overflow-hidden shadow-2xl z-50"
                style={{ background: '#0F1626', border: '1px solid rgba(14,165,233,0.25)' }}
                data-testid="assistant-plus-menu"
              >
                {showProjects ? (
                  <div className="max-h-72 overflow-y-auto">
                    <button onClick={() => setShowProjects(false)} className="w-full px-3 py-2 text-[11px] text-ai-text-secondary hover:bg-white/5 flex items-center gap-1.5 border-b border-white/5">
                      <ChevronRight className="w-3 h-3 rotate-180" /> Back
                    </button>
                    {projectsShortlist.length === 0 ? (
                      <p className="px-3 py-3 text-[11px] text-white/40 italic">No projects yet.</p>
                    ) : projectsShortlist.map(p => (
                      <button
                        key={p.id}
                        onClick={() => { setPlusOpen(false); setShowProjects(false); onClose && onClose(); navigate(`/project/${p.id}`); }}
                        className="w-full px-3 py-2 text-left text-[11px] text-white/85 hover:bg-white/5 flex items-center gap-2"
                        data-testid={`assistant-project-link-${p.id}`}
                      >
                        <Folder className="w-3 h-3 text-ai-accent flex-shrink-0" />
                        <span className="truncate">{p.name}</span>
                        <span className="ml-auto text-[9px] text-white/30 flex-shrink-0">{p.current_stage}/17</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => { fileInputRef.current?.click(); }}
                      className="w-full px-3 py-2.5 text-left text-xs text-white/85 hover:bg-white/5 flex items-center gap-2"
                      data-testid="assistant-menu-attach"
                    >
                      <Paperclip className="w-3.5 h-3.5 text-ai-accent" /> Add photos & files
                    </button>
                    <button
                      onClick={() => { setMode('image'); setPlusOpen(false); }}
                      className="w-full px-3 py-2.5 text-left text-xs text-white/85 hover:bg-white/5 flex items-center gap-2"
                      data-testid="assistant-menu-create-image"
                    >
                      <ImageIcon className="w-3.5 h-3.5 text-amber-300" /> Create image
                    </button>
                    <button
                      onClick={openProjectsMenu}
                      className="w-full px-3 py-2.5 text-left text-xs text-white/85 hover:bg-white/5 flex items-center gap-2"
                      data-testid="assistant-menu-projects"
                    >
                      <Folder className="w-3.5 h-3.5 text-emerald-300" /> Projects <ChevronRight className="w-3 h-3 text-white/30 ml-auto" />
                    </button>
                  </>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              hidden
              onChange={handleAttach}
              data-testid="assistant-file-input"
            />
          </div>

          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={mode === 'image' ? 'Describe the image…' : 'Ask anything'}
            disabled={sending}
            className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none px-1 disabled:opacity-50"
            data-testid="assistant-input"
          />

          <button
            onClick={sendMessage}
            disabled={sending || (!input.trim() && attachments.length === 0)}
            className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            style={{ background: '#0EA5E9' }}
            data-testid="assistant-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
          </button>
        </div>
      </div>
    </aside>
  );
}

export default EipiAssistantPanel;

// Mini progress component — counts seconds during image gen so user knows
// the request is still alive on slow mobile networks where 30-90s waits
// are common for gpt-image-2.
function ImageGenProgress() {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const phase = elapsed < 15 ? 'Generating image' : elapsed < 45 ? 'Still rendering — gpt-image-2 takes 30-60s' : 'Finalising upload';
  return <span>{phase}… <span className="opacity-60">({elapsed}s)</span></span>;
}

// Inline progress tag inside a message bubble while the async image job runs.
// Reads the message's `created_at` so the timer is accurate even if the user
// closes + reopens the panel mid-flight.
function PendingImageStatus({ startedAt }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.max(0, Math.round((now - new Date(startedAt).getTime()) / 1000));
  const phase = elapsed < 15 ? 'Generating' : elapsed < 60 ? 'Rendering' : 'Finalising';
  return <span>{phase} image… ({elapsed}s)</span>;
}
